import { Link, createFileRoute } from "@tanstack/react-router";
import { Trans } from "@lingui/react/macro";
import { Check } from "lucide-react";
import type { ReactNode } from "react";

import LandingLayout from "../components/LandingLayout";
import { clearBuilderFlowState } from "../lib/builderFlowStorage";
import {
  formatLocalizedPrice,
  getServerLocalizedPrice,
} from "../lib/localizedPrice";

export const Route = createFileRoute("/prices")({
  component: PreciosPage,
  head: () => ({
    meta: [
      {
        title: "BodaWeb — Precios",
      },
      {
        content:
          "Crea tu invitación de boda gratis o desbloquea funciones avanzadas con BodaWeb Premium. Cancela cuando quieras.",
        name: "description",
      },
    ],
  }),
  // Awaited in the loader so the resolved price is baked into the SSR HTML
  // (no client-side roundtrip, no em-dash flicker). Adds the Stripe API call
  // to TTFB (~150ms p50). Deferred streaming was attempted but TanStack
  // Router 1.159's serializer doesn't emit the resolution chunk for promises
  // that resolve before the SSR flush, so the client `use()` would suspend
  // indefinitely and the SSR'd price would be clobbered by the fallback.
  loader: async () => ({
    localizedPrice: await getServerLocalizedPrice(),
  }),
});

interface TierCardProps {
  badge?: ReactNode;
  cta: ReactNode;
  ctaTo: string;
  description: ReactNode;
  features: Array<{
    note?: ReactNode;
    text: ReactNode;
  }>;
  highlighted?: boolean;
  name: ReactNode;
  price: ReactNode;
  priceCadence?: ReactNode;
  testId: string;
}

function TierCard({
  badge,
  cta,
  ctaTo,
  description,
  features,
  highlighted = false,
  name,
  price,
  priceCadence,
  testId,
}: TierCardProps) {
  // The highlighted tier (Premium) gets the dark green accent treatment that
  // matches the homepage hero CTA. Free tier uses the default light card.
  const containerClass = highlighted
    ? "flex h-full flex-col bg-[var(--color-accent)] p-8 text-white sm:p-10"
    : "flex h-full flex-col border border-[var(--color-border)] bg-white p-8 sm:p-10";

  const eyebrowClass = highlighted
    ? "text-xs font-semibold uppercase tracking-[0.28em] text-white/60"
    : "text-xs font-semibold uppercase tracking-[0.28em] text-[var(--color-muted)]";

  const nameClass = highlighted
    ? "mt-3 font-display text-4xl italic leading-none text-white sm:text-5xl"
    : "mt-3 font-display text-4xl italic leading-none text-[var(--color-ink)] sm:text-5xl";

  const priceWrapClass = highlighted
    ? "mt-6 flex items-baseline gap-2 text-white"
    : "mt-6 flex items-baseline gap-2 text-[var(--color-ink)]";

  const cadenceClass = highlighted
    ? "text-sm uppercase tracking-[0.18em] text-white/60"
    : "text-sm uppercase tracking-[0.18em] text-[var(--color-muted)]";

  const descriptionClass = highlighted
    ? "mt-6 text-base leading-7 text-white/80"
    : "mt-6 text-base leading-7 text-[var(--color-muted)]";

  const checkClass = highlighted
    ? "mt-1 h-4 w-4 shrink-0 text-white"
    : "mt-1 h-4 w-4 shrink-0 text-[var(--color-accent)]";

  const featureTextClass = highlighted
    ? "text-white"
    : "text-[var(--color-ink)]";

  const featureNoteClass = highlighted
    ? "mt-1 text-sm text-white/60"
    : "mt-1 text-sm text-[var(--color-muted)]";

  const ctaClass = highlighted
    ? "app-btn-primary mt-10 w-full bg-white !text-[var(--color-accent)] hover:!bg-gray-100"
    : "app-btn-primary mt-10 w-full !text-white";

  return (
    <div className={containerClass} data-testid={testId}>
      <div>
        <p className={eyebrowClass}>{name}</p>
        {badge ? (
          <span
            className={
              highlighted
                ? "mt-3 inline-block bg-white/15 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-white"
                : "mt-3 inline-block bg-[var(--color-accent-soft)] px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-[var(--color-accent)]"
            }
          >
            {badge}
          </span>
        ) : null}
        <p className={nameClass}>{price}</p>
        <p className={priceWrapClass}>
          <span className={cadenceClass}>{priceCadence}</span>
        </p>
        <p className={descriptionClass}>{description}</p>
      </div>

      <ul className="mt-8 space-y-4 text-sm leading-6 sm:text-base">
        {features.map((feature, index) => (
          <li className="flex gap-3" key={index}>
            <Check
              aria-hidden="true"
              className={checkClass}
              strokeWidth={2.2}
            />
            <span>
              <span className={featureTextClass}>{feature.text}</span>
              {feature.note ? (
                <span className={`${featureNoteClass} block`}>
                  {feature.note}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-10">
        <Link
          className={ctaClass}
          data-testid={`${testId}-cta`}
          onClick={clearBuilderFlowState}
          search={{ step: undefined, template: undefined }}
          to={ctaTo}
        >
          {cta}
        </Link>
      </div>
    </div>
  );
}

function PreciosPage() {
  const { localizedPrice } = Route.useLoaderData();
  // Format "0" in the same currency as Premium so the Free card's symbol
  // matches (e.g. "0,00 €" alongside "9,99 €", "$0.00" alongside "$9.99").
  // Falls back to "$0" if the Stripe lookup failed, matching the "$9.99"
  // Premium fallback.
  const freePrice = localizedPrice
    ? formatLocalizedPrice(0, localizedPrice.currency)
    : "$0";

  return (
    <LandingLayout showFooter>
      <main className="overflow-x-hidden">
        <section className="border-b border-[var(--color-border)] bg-white">
          <div className="mx-auto max-w-4xl px-4 pb-14 pt-28 sm:px-6 sm:pb-16 sm:pt-32">
            <p className="text-xs font-semibold uppercase tracking-[0.34em] text-[var(--color-muted)]">
              <Trans>Precios</Trans>
            </p>
            <h1 className="mt-4 max-w-3xl font-display text-5xl italic leading-[0.92] tracking-[-0.03em] text-[var(--color-ink)] sm:text-6xl">
              <Trans>
                Empieza gratis. Súmate a Premium cuando lo necesites.
              </Trans>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--color-muted)]">
              <Trans>
                Puedes lanzar una invitación completa, compartirla por WhatsApp
                y recibir confirmaciones sin pagar nada. Premium es para parejas
                que quieren más generaciones con IA y una lista de invitados sin
                límite.
              </Trans>
            </p>
          </div>
        </section>

        <section
          className="mx-auto max-w-5xl px-4 py-14 sm:px-6 lg:py-20"
          data-testid="prices-tiers"
        >
          <div className="grid gap-6 md:grid-cols-2 md:gap-8">
            <TierCard
              cta={<Trans>Empezar gratis</Trans>}
              ctaTo="/create"
              description={
                <Trans>
                  Ideal para bodas con un grupo cercano de invitados. Sin
                  tarjeta de crédito, sin pruebas que vencen.
                </Trans>
              }
              features={[
                {
                  text: (
                    <Trans>
                      Acceso a todas las plantillas con animaciones y tipografía
                      premium.
                    </Trans>
                  ),
                },
                {
                  note: (
                    <Trans>
                      Descripciones, historias y secciones se reescriben con IA
                      hasta tres veces en total.
                    </Trans>
                  ),
                  text: (
                    <Trans>
                      3 generaciones de IA durante toda la vida del sitio.
                    </Trans>
                  ),
                },
                {
                  text: <Trans>Lista de invitados de hasta 25 personas.</Trans>,
                },
                {
                  text: (
                    <Trans>
                      Sitio publicado en{" "}
                      <span className="underline underline-offset-[0.22em]">
                        bodaweb.site
                      </span>{" "}
                      con enlace listo para WhatsApp.
                    </Trans>
                  ),
                },
                {
                  text: (
                    <Trans>
                      Edición ilimitada de fotos, textos y fechas después de
                      publicar.
                    </Trans>
                  ),
                },
              ]}
              name={<Trans>Plan gratuito</Trans>}
              price={freePrice}
              priceCadence={<Trans>siempre</Trans>}
              testId="prices-free"
            />

            <TierCard
              badge={<Trans>Más elegido</Trans>}
              cta={<Trans>Probar Premium</Trans>}
              ctaTo="/create"
              description={
                <Trans>
                  Para bodas con muchos invitados, varios cambios de planes y
                  parejas que quieren crear sus propias secciones para la web.
                </Trans>
              }
              features={[
                {
                  text: <Trans>Todo lo del plan gratuito.</Trans>,
                },
                {
                  note: (
                    <Trans>
                      30 generaciones por día — más que suficiente para iterar.
                    </Trans>
                  ),
                  text: (
                    <Trans>
                      Generación de secciones con IA prácticamente ilimitada.
                    </Trans>
                  ),
                },
                {
                  text: <Trans>Lista de invitados sin límite.</Trans>,
                },
                {
                  text: (
                    <Trans>
                      Exportación de la lista de invitados a CSV en un clic.
                    </Trans>
                  ),
                },
                {
                  note: <Trans>Próximamente.</Trans>,
                  text: (
                    <Trans>Lista de regalos y dominios personalizados.</Trans>
                  ),
                },
                {
                  text: (
                    <Trans>
                      Soporte prioritario por correo cuando lo necesites.
                    </Trans>
                  ),
                },
              ]}
              highlighted
              name={<Trans>Plan Premium</Trans>}
              price={localizedPrice?.formatted ?? "$9.99"}
              priceCadence={<Trans>al mes</Trans>}
              testId="prices-premium"
            />
          </div>
        </section>

        <section
          className="border-t border-[var(--color-border)] bg-[var(--color-page-soft)] py-16 lg:py-20"
          data-testid="prices-faq"
        >
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[var(--color-muted)]">
              <Trans>Preguntas frecuentes</Trans>
            </p>
            <h2 className="mt-4 font-display text-4xl italic leading-none text-[var(--color-ink)] sm:text-5xl">
              <Trans>Lo que las parejas suelen preguntar.</Trans>
            </h2>

            <div className="mt-10 grid gap-8 md:grid-cols-2">
              <div>
                <h3 className="font-display text-2xl italic leading-tight text-[var(--color-ink)]">
                  <Trans>¿Tengo que pagar para publicar mi web?</Trans>
                </h3>
                <p className="mt-3 text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                  <Trans>
                    No. El plan gratuito te permite publicar la invitación
                    completa, compartirla y gestionar una lista de hasta 25
                    invitados sin poner una tarjeta.
                  </Trans>
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl italic leading-tight text-[var(--color-ink)]">
                  <Trans>¿Puedo cancelar Premium cuando quiera?</Trans>
                </h3>
                <p className="mt-3 text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                  <Trans>
                    Sí, en cualquier momento. La suscripción es mensual y al
                    cancelarla tu sitio vuelve al plan gratuito: conservas la
                    web publicada, las primeras 25 confirmaciones de tu lista de
                    invitados y hasta 3 secciones generadas con IA. El resto de
                    los datos quedan guardados y se vuelven a desbloquear si
                    reactivas Premium más adelante.
                  </Trans>
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl italic leading-tight text-[var(--color-ink)]">
                  <Trans>¿Qué pasa si supero los 25 invitados?</Trans>
                </h3>
                <p className="mt-3 text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                  <Trans>
                    Tus invitados pueden seguir confirmando: las respuestas a
                    partir del invitado 26 se guardan automáticamente. Para
                    verlas y gestionarlas en el panel, basta con activar Premium
                    — el acceso al historial completo se desbloquea al instante.
                  </Trans>
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl italic leading-tight text-[var(--color-ink)]">
                  <Trans>¿Las plantillas son distintas en Premium?</Trans>
                </h3>
                <p className="mt-3 text-sm leading-7 text-[var(--color-muted)] sm:text-base">
                  <Trans>
                    No. El catálogo completo está disponible en ambos planes.
                    Premium desbloquea funciones de gestión, no diseños
                    diferentes.
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </LandingLayout>
  );
}
